/**
 * aircraft — the English half, typed against the German module, so a
 * missing or misspelled key is a type error in this file.
 */
import type { aircraft as de_aircraft } from '../de/aircraft';

export const aircraft: Record<keyof typeof de_aircraft, string> = {
  'ac.title': 'My aircraft',
  'ac.lead':
    'A profile describes this one aircraft. A preset only supplies starting values for the type. Tick off whatever comes from the POH or the weighing report – only that counts as confirmed.',
  'ac.mine': 'Aircraft',
  'ac.add': 'Add aircraft',
  'ac.empty': 'No aircraft yet.',
  'ac.nonumbers': 'no figures entered yet',
  'ac.edit': 'Edit',
  'ac.delete': 'Delete',
  'ac.confirm.delete': 'Delete “{reg}”? Plans keep their values but are no longer fed from it.',
  'ac.cancel': 'Cancel',
  'ac.save': 'Save',
  'ac.busy': 'saving …',

  'ac.step1.title': 'Pick a type',
  'ac.step1.hint': 'The preset fills the form in. Every value stays editable.',
  'ac.search.label': 'Search types',
  'ac.search.ph': 'C172, Piper, DA40 …',
  'ac.search.none': 'No type found — add one “without a preset”.',
  'ac.nopreset': 'without a preset',

  'ac.step2.title': 'New aircraft',
  'ac.step2.edit': 'Edit aircraft',
  'ac.sec.id': 'Aircraft',
  'ac.reg': 'Registration',
  'ac.reg.hint': 'Stored in upper case.',
  'ac.reg.ph': 'D-EAZA',
  'ac.icao': 'Type (ICAO)',
  'ac.type_label': 'Type (plain text)',

  'ac.sec.cruise': 'Cruise',
  'ac.sec.cruise.code': 'TAS · fuel flow',
  'ac.sec.cruise.hint': 'These two fill the per-leg TAS and the trip, reserve and endurance fuel figures.',
  'ac.sec.climb': 'Climb',
  'ac.sec.fuel': 'Fuel',
  'ac.sec.fuel.hint': 'This airframe’s quantities — its tank fit and its handbook, not a catalogue.',
  'ac.sec.mass': 'Mass & balance',
  'ac.sec.mass.hint':
    'Empty mass and empty moment are in this aircraft’s weighing report. The preset only gives a typical order of magnitude.',
  'ac.sec.other': 'Other',

  'ac.f.tas': 'TAS',
  'ac.f.lph': 'Fuel flow',
  'ac.f.power': 'Power',
  'ac.f.alt': 'Altitude',
  'ac.f.climb_fpm': 'Rate of climb',
  'ac.f.climb_lph': 'Fuel flow in the climb',
  'ac.f.usable': 'Usable fuel',
  'ac.f.unusable': 'Unusable fuel',
  'ac.f.unusable.hint': 'Carried as a mass: litres × 0.72 kg/l (avgas).',
  'ac.f.taxi': 'Start-up and taxi',
  'ac.f.reserve': 'Reserve',
  'ac.f.reserve.hint': 'Default 45 min. The legal minimum depends on the operation (NCO.OP.125) — please check.',
  'ac.f.empty_kg': 'Empty mass',
  'ac.f.empty_mom': 'Empty moment',
  'ac.f.empty_arm': 'Empty arm',
  'ac.f.mtow': 'Maximum take-off mass',
  'ac.f.unit_mass': 'Mass unit',
  'ac.f.unit_moment': 'Moment unit',
  'ac.f.seats': 'Seats',
  'ac.f.notes': 'Notes',
  'ac.f.source': 'Preset per {note}',

  'ac.cruisetable': 'Cruise table from the preset',
  'ac.cruisetable.hint':
    'Taken from the preset. The highest-power row supplies the TAS and fuel flow above when those are left empty.',
  'ac.cruisetable.drop': 'Discard table',

  'ac.confirm.field': 'confirmed from POH / weighing report',
  'ac.badge.confirmed': 'confirmed',
  'ac.badge.preset': 'Preset (estimate)',
  'ac.badge.preset.short': 'preset',
  'ac.badge.unconfirmed': 'unconfirmed',

  'ac.meta.tas': 'TAS {v} kt',
  'ac.meta.lph': '{v} l/h',
  'ac.meta.usable': '{v} l usable',
  'ac.meta.mtow': 'MTOM {v} kg',
  'ac.meta.seats': '{v} seats',

  'ac.err.reg': 'Registration missing or too long (3–12 characters).',
  'ac.err.numbers': 'At least one field is not a valid number, or is outside the allowed range.',
  'ac.err.mass': 'The empty mass is greater than the maximum take-off mass.',
  'ac.field.derived': 'from the aircraft profile {reg}',
  'ac.field.derived.est': 'from the aircraft profile {reg} – preset value, not confirmed',
};
